import { MlFeature, MlTarget } from "../../components/ml/types";
import { IGroupByListItem } from "../../models";
import formatPropertyName from "../column/formatPropertyName";
import getColumnLabelWithContext from "../column/getColumnLabelWithContext";
import getGroupByListColumnByProperty from "../column/getGroupByListColumnByProperty";

// Strip all unit suffixes present in the feature's own unit map from a name string.
// This is the primary safety net — it works even when the column lookup fails.
function stripItemUnits<T extends MlFeature | MlTarget>(item: T, name: string): string {
  const unitMap = (item as MlFeature).unit;
  if (!unitMap) return name;
  let result = name;
  for (const unit of Object.values(unitMap)) {
    if (unit) result = result.replace(` (${unit})`, "");
  }
  return result;
}

/**
 * Formats an ML feature or target name using getColumnLabelWithContext.
 * Handles special cases:
 * - Sampled 3D Geo features: preserves unit in NiceName and assigns grouping from metadata.radius
 * - Other features: removes defaultUnit from NiceName
 *
 * @param item - The ML feature or target to format
 * @param categoryList - The category list containing column definitions
 * @returns The formatted feature/target with updated NiceName and optional grouping
 */
export function formatMlFeatureName<T extends MlFeature | MlTarget>(
  item: T,
  categoryList: IGroupByListItem[]
): T {
  const col = getGroupByListColumnByProperty(
    categoryList,
    formatPropertyName(item.Property)
  );

  if (!col) {
    // Column not in categoryList — still strip via item.unit to prevent leaked suffixes
    return { ...item, NiceName: stripItemUnits(item, item.NiceName) };
  }

  // Global normalization doesn't apply in ML context
  const contextTitle = getColumnLabelWithContext(col, false, undefined);

  // For Sampled 3D Geo features, assign grouping and keep NiceName without unit
  if (col.group === "Sampled 3D Geo" && col.metadata?.radius) {
    return {
      ...item,
      NiceName: item.NiceName,
      grouping: col.metadata.radius
    };
  }

  // For other features, remove the unit from the formatted name.
  // Strip defaultUnit (SI) and all display units from col.units so that
  // imperial/hybrid unit suffixes (e.g. "(ft)") don't leak into the backend.
  let niceNameWithoutUnit = contextTitle.fullContextTitle || item.NiceName;
  if (col.defaultUnit) {
    niceNameWithoutUnit = niceNameWithoutUnit.replace(` (${col.defaultUnit})`, "");
  }
  if (Array.isArray(col.units)) {
    for (const unit of col.units) {
      if (unit) {
        niceNameWithoutUnit = niceNameWithoutUnit.replace(` (${unit})`, "");
      }
    }
  }
  // Belt-and-suspenders: also strip via item.unit (covers null col.units)
  niceNameWithoutUnit = stripItemUnits(item, niceNameWithoutUnit);

  return {
    ...item,
    NiceName: niceNameWithoutUnit
  };
}
